import type {
  AppSettings,
  AttendanceRecord,
  AttendanceStatus,
  DemoUser,
  SchoolClass,
  Student,
} from "../types";
import { dateOffsetISO, initials, todayISO } from "../lib/utils";

const streams = ["Amanah", "Bestari"];
const teachers = [
  "Cikgu Farah Nabilah",
  "Cikgu Daniel Liew",
  "Cikgu Nurul Izzati",
  "Cikgu Jonathan Goh",
  "Cikgu Aina Rosli",
  "Cikgu Melissa Jaimin",
  "Cikgu Siti Hajar",
  "Cikgu Adrian Lojing",
  "Cikgu Maisarah Latif",
  "Cikgu Kelvin Wong",
  "Cikgu Hidayah Murni",
  "Cikgu Marcus Majalang",
];

export const schoolClasses: SchoolClass[] = Array.from({ length: 12 }, (_, index) => {
  const year = Math.floor(index / 2) + 1;
  const stream = streams[index % 2];
  const teacher = teachers[index];
  return {
    id: `class-${year}-${stream.toLowerCase()}`,
    name: `${year} ${stream}`,
    year,
    stream,
    room: `Bilik ${String.fromCharCode(65 + index)}`,
    teacher,
    teacherInitials: initials(teacher.replace("Cikgu ", "")),
    studentCount: 26,
  };
});

const maleNames = [
  "Muhammad Adam",
  "Aqil Danish",
  "Rayyan Hakim",
  "Aaron Jefri",
  "Joshua Lee",
  "Daniel Wong",
  "Irfan Harith",
  "Nathaniel Lim",
  "Ethan Jaimin",
  "Amirul Fikri",
  "Haziq Imran",
  "Lucas Goh",
  "Khairol Azwan",
  "Jovan Matius",
  "Rizqi Aiman",
  "Gabriel Tan",
  "Zafran Iqbal",
  "Hafiz Arif",
  "Ryan Lojing",
  "Adrian Majalang",
  "Syafiq Rizwan",
  "Edmund Lai",
  "Amsyar Faris",
  "Isaac Chong",
  "Darren Ong",
  "Naufal Zikri",
];

const femaleNames = [
  "Nur Aisyah",
  "Alya Sofea",
  "Siti Hawa",
  "Hannah Lim",
  "Grace Wong",
  "Qaisara Imani",
  "Olivia Jaimin",
  "Puteri Balqis",
  "Chloe Lee",
  "Maryam Zahra",
  "Sofia Adriana",
  "Ariana Goh",
  "Nur Amirah",
  "Joanne Tan",
  "Elisha Lojing",
  "Dayana Izzati",
  "Kimberly Lai",
  "Sarah Chong",
  "Nurin Batrisyia",
  "Elena Majalang",
  "Adelia Maisarah",
  "Evelyn Ong",
  "Damia Husna",
  "Victoria Liew",
  "Insyirah Hana",
  "Natalie Goh",
];

const fatherNames = [
  "Azlan",
  "Rahman",
  "Hakim",
  "Rosli",
  "Johan",
  "Fadzil",
  "Kassim",
  "Ismail",
  "Ramli",
  "Hassan",
  "Liew",
  "Jaimin",
  "Majalang",
  "Lojing",
  "Tan",
  "Wong",
  "Lee",
  "Goh",
  "Chong",
  "Ong",
  "Lai",
  "Matius",
  "Jefri",
  "Karim",
  "Yusof",
  "Latif",
];

const neighbourhoods = [
  "Taman Papar Jaya",
  "Kampung Limbahau",
  "Taman Benoni",
  "Kampung Kawang",
  "Taman Pantai Manis",
  "Kampung Beringgis",
  "Taman Seri Papar",
  "Kampung Kelatuan",
];

const occupations = [
  "Kakitangan kerajaan",
  "Peniaga",
  "Petani",
  "Juruteknik",
  "Pembantu perubatan",
  "Pemandu",
  "Guru",
  "Bekerja sendiri",
];

const buildStudentName = (index: number, gender: Student["gender"]) => {
  const first = gender === "Lelaki" ? maleNames[index % maleNames.length] : femaleNames[index % femaleNames.length];
  const father = fatherNames[(index * 7) % fatherNames.length];
  const connector = gender === "Lelaki" ? "bin" : "binti";
  return `${first} ${connector} ${father}`;
};

export const generateStudents = (): Student[] =>
  schoolClasses.flatMap((schoolClass, classIndex) =>
    Array.from({ length: schoolClass.studentCount }, (_, position) => {
      const index = classIndex * 26 + position;
      const gender: Student["gender"] = index % 2 === 0 ? "Lelaki" : "Perempuan";
      const name = buildStudentName(index + classIndex * 3, gender);
      const sequence = index + 1;
      const birthYear = new Date().getFullYear() - (6 + schoolClass.year);
      const month = String((index % 12) + 1).padStart(2, "0");
      const day = String((index % 24) + 2).padStart(2, "0");
      const code = `STJ-${new Date().getFullYear()}-${String(sequence).padStart(4, "0")}`;
      const guardianBase = fatherNames[(index * 5 + 3) % fatherNames.length];
      const guardianName = gender === "Lelaki" ? `${guardianBase} Abdullah` : `Nor ${guardianBase}`;
      return {
        id: `student-${String(sequence).padStart(4, "0")}`,
        code,
        qrPayload: `JOSEPHCARE|${code}|v1`,
        name,
        initials: initials(name),
        classId: schoolClass.id,
        className: schoolClass.name,
        gender,
        birthDate: `${birthYear}-${month}-${day}`,
        myKidMasked: `${String(birthYear).slice(2)}${month}${day}-12-••••`,
        nationality: "Malaysia",
        religion: index % 5 === 0 ? "Kristian" : "Islam",
        address: `No. ${(index % 48) + 1}, ${neighbourhoods[index % neighbourhoods.length]}, 89600 Papar, Sabah`,
        guardian: {
          name: guardianName,
          relation: index % 3 === 0 ? "Bapa" : "Ibu",
          phone: `01${(index % 8) + 1}-${String(7000000 + index).slice(0, 7)}`,
          email: `penjaga${String(sequence).padStart(3, "0")}@contoh.my`,
          occupation: occupations[index % occupations.length],
        },
        secondaryGuardian: {
          name: `${fatherNames[(index + 11) % fatherNames.length]} ${fatherNames[(index + 7) % fatherNames.length]}`,
          relation: "Penjaga",
          phone: `01${((index + 3) % 8) + 1}-${String(7100000 + index).slice(0, 7)}`,
          email: `waris${String(sequence).padStart(3, "0")}@contoh.my`,
          occupation: occupations[(index + 3) % occupations.length],
        },
        emergencyContact: `088-${String(700000 + index).slice(0, 6)}`,
        medical: {
          bloodType: ["O+", "A+", "B+", "AB+"][index % 4],
          allergies: index % 19 === 0 ? "Kacang" : index % 23 === 0 ? "Habuk" : "Tiada direkod",
          conditions: index % 37 === 0 ? "Asma ringan" : "Tiada direkod",
          notes: index % 37 === 0 ? "Inhaler disimpan di bilik rawatan." : "Tiada arahan khas.",
        },
        attendanceRate: 88 + ((index * 7) % 12),
        active: index !== 311,
        joinedAt: `${new Date().getFullYear()}-01-03`,
      };
    }),
  );

const seededStatus = (index: number, dayOffset: number): AttendanceStatus => {
  const seed = index + Math.abs(dayOffset) * 13;
  if (seed % 43 === 0) return "absent";
  if (seed % 29 === 0) return "late";
  if (seed % 61 === 0) return "excused";
  return "present";
};

export const generateAttendance = (students: Student[]): AttendanceRecord[] => {
  const records: AttendanceRecord[] = [];
  for (let dayOffset = -5; dayOffset <= 0; dayOffset += 1) {
    const date = dayOffset === 0 ? todayISO() : dateOffsetISO(dayOffset);
    students.forEach((student, index) => {
      const positionInClass = index % 26;
      if (dayOffset === 0 && positionInClass === 25) return;
      const status = seededStatus(index, dayOffset);
      const minute = (index * 7 + Math.abs(dayOffset)) % 44;
      const hour = status === "late" ? 7 : 6;
      records.push({
        id: `attendance-${date}-${student.id}`,
        studentId: student.id,
        date,
        timestamp: `${date}T${String(hour).padStart(2, "0")}:${String(15 + minute).padStart(2, "0")}:00+08:00`,
        status,
        method: index % 11 === 0 ? "manual" : "qr",
        recordedBy: index % 11 === 0 ? "Pn. Rozita Karim" : schoolClasses.find((item) => item.id === student.classId)?.teacher ?? "Guru kelas",
        note: status === "excused" ? "Makluman penjaga diterima." : undefined,
      });
    });
  }
  return records;
};

export const demoUsers: Record<"admin" | "teacher", DemoUser> = {
  admin: {
    id: "user-admin",
    name: "Pn. Rozita Karim",
    role: "admin",
    title: "Pentadbir Sekolah",
    initials: "RK",
  },
  teacher: {
    id: "user-teacher",
    name: "Cikgu Aina Rosli",
    role: "teacher",
    title: "Guru Kelas · 3 Amanah",
    initials: "AR",
    assignedClassId: "class-3-amanah",
  },
};

export const defaultSettings: AppSettings = {
  schoolName: "SK St Joseph (M) Papar",
  schoolShortName: "SK St Joseph",
  lateAfter: "07:10",
  scanCooldownSeconds: 4,
  confirmStudentPhoto: true,
  playScanSound: true,
  cameraEnabled: true,
  offlineEnabled: true,
  requireLocation: false,
  autoNotifyGuardian: false,
  attendanceStatuses: ["present", "late", "absent", "excused"],
  permissions: {
    teacherCanViewMedical: true,
    teacherCanViewGuardianPhone: true,
    teacherCanEditAttendance: false,
    teacherCanDownloadQr: false,
    teacherCanViewOtherClasses: false,
  },
};

